import type { App } from 'vue'
import useUIStore from '@/stores/modules/ui'

/**
 * 显示登录弹窗
 * @param inviteCode 邀请码（可选）
 */
function showLoginDialog(inviteCode = '') {
  const uiStore = useUIStore()
  uiStore.showAuthDialog(true, inviteCode)
}

/**
 * 显示注册弹窗
 * @param inviteCode 邀请码（可选）
 */
function showRegisterDialog(inviteCode = '') {
  const uiStore = useUIStore()
  uiStore.showAuthDialog(false, inviteCode)
}

/**
 * 隐藏认证弹窗
 */
function hideAuthDialog() {
  const uiStore = useUIStore()
  uiStore.hideAuthDialog()
}

/**
 * 切换认证弹窗模式
 * @param isLogin 是否为登录模式
 */
function switchAuthMode(isLogin: boolean) {
  const uiStore = useUIStore()
  uiStore.switchAuthMode(isLogin)
}

/**
 * 设置认证弹窗的邀请码
 * @param inviteCode 邀请码
 */
function setAuthInviteCode(inviteCode: string) {
  const uiStore = useUIStore()
  uiStore.setAuthInviteCode(inviteCode)
}

// 过渡效果类型定义
interface TransitionOptions {
  type: 'heartbeat' | 'hover-scale' | 'drag-throw' | 'bounce' | 'shake' | 'pulse'
  duration?: number
  scale?: number
  intensity?: number
  delay?: number
}

// 过渡指令实现
const transitionCommand = {
  mounted(el: HTMLElement, binding: { value?: TransitionOptions | string }) {
    // 处理不同的传参方式
    let options: TransitionOptions

    if (typeof binding.value === 'string') {
      // 如果直接传字符串，则作为效果类型
      options = { type: binding.value as any }
    } else if (typeof binding.value === 'object' && binding.value !== null) {
      // 如果传对象，则使用对象配置
      options = binding.value
    } else {
      // 如果不传参，默认使用心跳效果
      options = { type: 'heartbeat' }
    }

    const { type = 'heartbeat', duration = 150, scale = 1.15, intensity = 1, delay = 0 } = options

    // 添加基础过渡样式
    el.style.transition = `all ${duration}ms ease-in-out`
    el.style.cursor = 'pointer'

    // 根据不同类型绑定不同的事件
    switch (type) {
      case 'heartbeat':
        bindHeartbeatEffect(el, duration, scale)
        break
      case 'hover-scale':
        bindHoverScaleEffect(el, duration, scale)
        break
      case 'drag-throw':
        bindDragThrowEffect(el, duration, intensity)
        break
      case 'bounce':
        bindBounceEffect(el, duration, intensity)
        break
      case 'shake':
        bindShakeEffect(el, duration, intensity)
        break
      case 'pulse':
        bindPulseEffect(el, duration, scale)
        break
      default:
        // 如果类型不匹配，默认使用心跳效果
        bindHeartbeatEffect(el, duration, scale)
        break
    }
  },

  updated(el: HTMLElement, binding: { value?: TransitionOptions | string }) {
    // 当指令值更新时重新绑定
    transitionCommand.mounted(el, binding)
  }
}

// 心跳效果
function bindHeartbeatEffect(el: HTMLElement, duration: number, scale: number) {
  el.addEventListener('click', () => {
    el.style.transform = `scale(${scale})`
    setTimeout(() => {
      el.style.transform = 'scale(1)'
    }, duration)
  })
}

// 悬停放大效果
function bindHoverScaleEffect(el: HTMLElement, duration: number, scale: number) {
  el.addEventListener('mouseenter', () => {
    el.style.transform = `scale(${scale})`
  })

  el.addEventListener('mouseleave', () => {
    el.style.transform = 'scale(1)'
  })
}

// 拖拽抛出效果
function bindDragThrowEffect(el: HTMLElement, duration: number, intensity: number) {
  let isDragging = false
  let startX = 0
  let startY = 0

  el.addEventListener('mousedown', (e) => {
    isDragging = true
    startX = e.clientX
    startY = e.clientY
    el.style.cursor = 'grabbing'
  })

  el.addEventListener('mousemove', (e) => {
    if (!isDragging) return

    const deltaX = e.clientX - startX
    const deltaY = e.clientY - startY
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

    if (distance > 50) {
      el.style.transform = `translate(${deltaX * 0.1}px, ${deltaY * 0.1}px) rotate(${deltaX * 0.1}deg)`
    }
  })

  el.addEventListener('mouseup', () => {
    if (isDragging) {
      isDragging = false
      el.style.cursor = 'pointer'
      el.style.transform = 'translate(0, 0) rotate(0deg)'
    }
  })

  el.addEventListener('mouseleave', () => {
    if (isDragging) {
      isDragging = false
      el.style.cursor = 'pointer'
      el.style.transform = 'translate(0, 0) rotate(0deg)'
    }
  })
}

// 弹跳效果
function bindBounceEffect(el: HTMLElement, duration: number, intensity: number) {
  el.addEventListener('click', () => {
    el.style.animation = `bounce ${duration}ms ease-in-out`
    setTimeout(() => {
      el.style.animation = ''
    }, duration)
  })
}

// 震动效果
function bindShakeEffect(el: HTMLElement, duration: number, intensity: number) {
  el.addEventListener('click', () => {
    el.style.animation = `shake ${duration}ms ease-in-out`
    setTimeout(() => {
      el.style.animation = ''
    }, duration)
  })
}

// 脉冲效果
function bindPulseEffect(el: HTMLElement, duration: number, scale: number) {
  el.addEventListener('click', () => {
    el.style.transform = `scale(${scale})`
    setTimeout(() => {
      el.style.transform = 'scale(1)'
    }, duration / 2)
    setTimeout(() => {
      el.style.transform = `scale(${scale})`
    }, duration / 2 + 50)
    setTimeout(() => {
      el.style.transform = 'scale(1)'
    }, duration)
  })
}

export default {
  install(app: App) {
    // 注册过渡指令
    app.directive('trans', transitionCommand)

    // 注册为全局属性
    app.config.globalProperties.$showLoginDialog = showLoginDialog
    app.config.globalProperties.$showRegisterDialog = showRegisterDialog
    app.config.globalProperties.$hideAuthDialog = hideAuthDialog
    app.config.globalProperties.$switchAuthMode = switchAuthMode
    app.config.globalProperties.$setAuthInviteCode = setAuthInviteCode

    // 同时注册到 window 对象，方便在任何地方调用
    window.$showLoginDialog = showLoginDialog
    window.$showRegisterDialog = showRegisterDialog
    window.$hideAuthDialog = hideAuthDialog
    window.$switchAuthMode = switchAuthMode
    window.$setAuthInviteCode = setAuthInviteCode

    // 添加必要的CSS动画
    addTransitionStyles()
  }
}

// 添加过渡动画的CSS样式
function addTransitionStyles() {
  if (typeof document === 'undefined') return

  const style = document.createElement('style')
  style.textContent = `
    @keyframes bounce {
      0%, 20%, 53%, 80%, 100% {
        transform: translate3d(0, 0, 0);
      }
      40%, 43% {
        transform: translate3d(0, -8px, 0);
      }
      70% {
        transform: translate3d(0, -4px, 0);
      }
      90% {
        transform: translate3d(0, -2px, 0);
      }
    }

    @keyframes shake {
      0%, 100% {
        transform: translateX(0);
      }
      10%, 30%, 50%, 70%, 90% {
        transform: translateX(-2px);
      }
      20%, 40%, 60%, 80% {
        transform: translateX(2px);
      }
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
      }
    }
  `
  document.head.appendChild(style)
}
